/**
 * WHETHER A CHECK COULD RUN IN FULL (vc-impl a05ef33f8). Every completeness
 * run is stored; one that could not see everything it needed is marked
 * `complete: false` with `incomplete_json` {code, missing, rules, reason}
 * (a complete one has it null). Such a record is never shown as passed: it
 * reads "Incomplete: <what was missing>", the reason, and the rules not
 * checked, by their labels; a code or rule name only when a label is missing.
 * A record without the field is from before the change, and complete.
 */
import React from "react";
import { NOTED_WIRE } from "@sourcepilot/ui/framework/data/pins/noted-conflicts";
import { OVERRIDE_WIRE } from "@sourcepilot/ui/framework/data/pins/override-drops";
import { ConflictingPinsNote } from "@sourcepilot/ui/framework/ui/molecules/NotedConflicts";

type Labelled = { code?: string; rule?: string; label?: string; details?: unknown };
type Incomplete = { code?: string; missing?: Labelled[]; rules?: Labelled[]; reason?: string };

function parse(json: unknown): Incomplete | null {
  if (json && typeof json === "object") return json as Incomplete;
  if (typeof json !== "string" || !json) return null;
  try {
    const v = JSON.parse(json);
    return v && typeof v === "object" ? (v as Incomplete) : null;
  } catch {
    return null;
  }
}

/** A code or rule name in words: "_requirement_uses_undefined_term" →
 *  "requirement uses undefined term". Only when there is no label. */
const inWords = (key: string) => key.replace(/_+/g, " ").trim().toLowerCase();

/** Each entry's label; its code (or rule name) in words only when the label is missing. */
function labels(list: unknown): string[] {
  return (Array.isArray(list) ? list : [])
    .map((e: Labelled) => (e?.label ? e.label : e?.code ? inWords(e.code) : e?.rule ? inWords(e.rule) : null))
    .filter((t): t is string => !!t);
}

/** What was missing, in words. */
export function missingWords(node: { incomplete_json?: unknown } | null | undefined): string {
  const inc = parse(node?.incomplete_json);
  const items = labels(inc?.missing);
  if (items.length > 0) return items.join(", ");
  return inc?.code ? inWords(inc.code) : "some inputs were not available";
}

export const isIncomplete = (node: { complete?: unknown } | null | undefined): boolean =>
  node?.complete === false;

export function AnalysisRecordCompleteness({ node }: { editable?: unknown; node: any }) {
  if (!isIncomplete(node)) {
    return <span className="text-sm text-gray-700" data-testid="analysis-complete">Complete</span>;
  }
  const inc = parse(node?.incomplete_json);
  const rules = labels(inc?.rules);
  // V33 revised (VC-D): it depends on a conflict only noted in this draft
  // ("incomplete: conflicting pins"), or on a link a kept override breaks
  // (its record is missing at the kept version) — in the server's
  // sentences, each naming the island and both versions, never a plain
  // failure
  const missing = Array.isArray(inc?.missing) ? inc!.missing : [];
  const noted = missing.filter((m) => m?.code === NOTED_WIRE.reportMissingCode);
  const dropped = missing.filter((m) => m?.code === OVERRIDE_WIRE.reportMissingCode);
  if (noted.length || dropped.length) {
    const sentences = [...noted, ...dropped].flatMap((m) => {
      const d = (m as Record<string, unknown>)[NOTED_WIRE.reportDetailsField];
      const said = Array.isArray(d) ? d.filter((x): x is string => typeof x === "string" && !!x) : [];
      return said.length ? said : m.label ? [m.label] : [];
    });
    return (
      <span className="block text-sm text-amber-700" data-testid="analysis-incomplete" data-code={noted.length ? NOTED_WIRE.reportMissingCode : OVERRIDE_WIRE.reportMissingCode}>
        <span data-testid="analysis-incomplete-missing">
          Incomplete: {[noted.length ? "conflicting pins" : null, dropped.length ? "links an override breaks" : null].filter(Boolean).join("; ")}
        </span>
        <ConflictingPinsNote sentences={sentences} testId="analysis-conflicting-pins" />
        {rules.length > 0 && (
          <span className="block text-xs text-gray-600" data-testid="analysis-incomplete-rules">
            Not checked: {rules.join(", ")}
          </span>
        )}
      </span>
    );
  }
  return (
    <span className="block text-sm text-amber-700" data-testid="analysis-incomplete">
      <span data-testid="analysis-incomplete-missing">Incomplete: {missingWords(node)}</span>
      {inc?.reason && (
        <span className="block text-xs text-gray-600" data-testid="analysis-incomplete-reason">{inc.reason}</span>
      )}
      {rules.length > 0 && (
        <span className="block text-xs text-gray-600" data-testid="analysis-incomplete-rules">
          Not checked: {rules.join(", ")}
        </span>
      )}
    </span>
  );
}

/** `incomplete_json` is shown through `complete`; never as raw JSON. */
export function AnalysisRecordIncompleteDetail() {
  return null;
}
